/**
 * Shipment Calendar redesign ("Shipment Calendar Redesign" design): a month or week calendar showing how busy each
 * day is, and the chosen day's shipments as cards. Pure helpers, so the rules can be tested without rendering.
 *
 * The data is the old calendar's: GET /api/shipmentCalendarView for the visible range, filtered by the checked
 * calendar_status labels' colours and an order no. Nothing about the request changes; only the page around it.
 */

import { authRoles } from 'app/auth';
import { easternDay } from 'app/main/apps/common/easternTime';
import { shipmentLoadPath } from 'app/main/apps/shipmentLoad/shipmentLoadRules';

function roleList(user) {
  const role = user?.role;
  if (Array.isArray(role)) return role.filter(Boolean);
  return role ? [role] : [];
}

/** Admin, staff or sales get the new calendar. Warehouse (the other role the route admits) keeps the old one. */
export function shouldUseShipmentCalendar(user) {
  const staff = authRoles?.staff || [];
  return roleList(user).some((role) => staff.includes(role));
}

/* ------------------------------------------------------------------ dates */

/** A local Date's calendar day as 'YYYY-MM-DD'. */
export function dayKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** 'YYYY-MM-DD' as a local Date at midnight. */
export function fromDayKey(key) {
  const [y, m, d] = String(key).split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

/** The Sunday that starts d's week. */
export function startOfWeek(d) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return addDays(x, -x.getDay());
}

export function sameDay(a, b) {
  return !!a && !!b && dayKey(a) === dayKey(b);
}

/** The month grid's days: whole weeks from the Sunday before the 1st, 5 or 6 rows. */
export function monthDays(cursor) {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const start = startOfWeek(first);
  let days = Array.from({ length: 42 }, (_, i) => addDays(start, i));
  while (days.length > 35 && days[days.length - 7].getMonth() !== first.getMonth()) days = days.slice(0, -7);
  return days;
}

export function weekDays(cursor) {
  const start = startOfWeek(cursor);
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

/** PGSS-5817: the year view's twelve months, as each month's 1st. */
export function yearMonths(cursor) {
  return Array.from({ length: 12 }, (_, m) => new Date(cursor.getFullYear(), m, 1));
}

/**
 * The range the API is asked for, in the shape the old calendar stored (FullCalendar's datesSet): the first
 * visible day and the day after the last one. calendarParams reads startStr / endStr.
 *
 * PGSS-5817: the year view asks for the whole year in one request, as the old calendar's Year view did
 * (multiMonthYear). Its end is the next Jan 1, like the other views' ends, so Dec 31 is in it.
 */
export function visibleRange(view, cursor) {
  if (view === 'year') {
    const year = cursor.getFullYear();
    return { view: { type: 'multiMonthYear', title: `Year ${year}` }, startStr: `${year}-01-01`, endStr: `${year + 1}-01-01` };
  }
  const days = view === 'week' ? weekDays(cursor) : monthDays(cursor);
  const type = view === 'week' ? 'timeGridWeek' : 'dayGridMonth';
  return {
    view: { type, title: view === 'week' ? 'Week' : 'Month' },
    startStr: dayKey(days[0]),
    endStr: dayKey(addDays(days[days.length - 1], 1)),
  };
}

/* ------------------------------------------------------------------ shipments */

/**
 * The four kinds the calendar_status labels select, by label name. The colour is the server's (ShipmentCalendarColour):
 * green shipped, red overdue, orange upcoming, purple an upcoming priority shipment.
 */
export const KINDS = ['overdue', 'priority', 'shipped', 'upcoming'];
export const KIND_LABEL = { overdue: 'Overdue', priority: 'Priority', shipped: 'Shipped', upcoming: 'Upcoming' };

/** One calendar event from /api/shipmentCalendarView, read for the page. Null when it has no shipment. */
export function toShipment(event) {
  const details = event?.extendsprops?.shipment;
  const first = Array.isArray(details) ? details[0] : null;
  const id = first?.shipment_id;
  const day = easternDay(event?.date);
  if (id === undefined || id === null || !day) return null;
  const color = String(event.color || '').toLowerCase();
  const shipped = color === 'green';
  const overdue = !shipped && (event.is_overdue === true || color === 'red');
  return {
    id,
    no: first.shipment_no || '',
    customer: event.title || first.customer_name || event.customer_name || '',
    day,
    status: first.status || null,
    shipmentType: first.shipment?.shipment_type || '',
    orders: details.length,
    shipped,
    overdue,
    upcoming: !shipped && !overdue,
    // A shipped priority shipment is just shipped (the server stops painting priority once it ships).
    priority: !shipped && event.is_priority === true,
    canChangeDate: event.allow_shipment_date_change === true,
    // PGSS-5861: what to do next and how far along it is (the server decides; null on an older server or a
    // supplier container, and the card then shows what it did before).
    nextAction: event.next_action || null,
    nextLabel: event.next_label || '',
    step: Number.isInteger(event.step) ? event.step : null,
    stepCount: Number.isInteger(event.step_count) ? event.step_count : null,
    event,
  };
}

/**
 * PGSS-5861: the card's progress bar, one segment per step of the shipment's type: 'done', 'current' or 'todo'.
 * Empty when the server sent no steps, or while the shipment waits for approval (step 0: nothing started).
 */
export function progressSegments(s) {
  const count = s?.stepCount;
  const step = s?.step;
  if (!Number.isInteger(count) || count <= 0 || !Number.isInteger(step) || step <= 0) return [];
  return Array.from({ length: count }, (_, i) => {
    const n = i + 1;
    if (n < step) return 'done';
    return n === step ? 'current' : 'todo';
  });
}

/**
 * PGSS-5861: where the card's "Continue" button goes, labelled with the server's next step. Picking hasn't started:
 * Scan to load, where it starts (for the roles that use it). Once it has, the card can't tell how far the load got
 * (the calendar doesn't read each load), so it opens the Check List, which shows the exact step and leads to Scan to
 * load if loading is still next (review of #2820). Null when there is nothing to continue: no next step, awaiting
 * approval (Approve is on the card), or shipped.
 */
export function continueTarget(s, canLoad) {
  const action = s?.nextAction;
  if (!action || action === 'APPROVE' || action === 'DONE' || s.shipped) return null;
  const label = s.nextLabel || 'Continue';
  if (action === 'PICK' && canLoad) return { label, to: shipmentLoadPath(s.id), scan: true };
  return { label, to: `/shipments/details/${s.id}`, scan: false };
}

export function toShipments(events) {
  return (Array.isArray(events) ? events : []).map(toShipment).filter(Boolean);
}

/** Order numbers start with a digit (13280, 13280-S1); customers and shipment nos (SHIP-3317) don't. */
export function isOrderNoQuery(q) {
  return /^\d/.test(String(q || '').trim());
}

/** The search box: customer or shipment no, here. An order no is the server's (order_no), so it is not matched here. */
export function matchesQuery(s, q) {
  const text = String(q || '').trim().toLowerCase();
  if (!text || isOrderNoQuery(text)) return true;
  return s.customer.toLowerCase().includes(text) || s.no.toLowerCase().includes(text);
}

/** A day's shipments: overdue first, then priority, then by customer. */
export function shipmentsOn(list, key) {
  return list
    .filter((s) => s.day === key)
    .sort((a, b) => b.overdue - a.overdue || b.priority - a.priority || a.customer.localeCompare(b.customer));
}

/**
 * The day a search moves to: the first matching day from today on, or else the latest one before today.
 * Null when nothing matches.
 */
export function searchTargetDay(list, todayKey) {
  const days = [...new Set(list.map((s) => s.day))].sort();
  if (!days.length) return null;
  return days.find((d) => d >= todayKey) || days[days.length - 1];
}

export function countKind(list, kind) {
  return list.filter((s) => s[kind]).length;
}

/* ------------------------------------------------------------------ labels */

/** The label (calendar_status) a kind's toggle stands for, by name. */
export function labelForKind(labels, kind) {
  const name = KIND_LABEL[kind].toLowerCase();
  return (labels || []).find((l) => String(l?.name || '').toLowerCase() === name) || null;
}

/**
 * Flips one kind's label. The last checked label cannot be unchecked: with none the server falls back to orange and
 * red, which would show shipments no toggle says are on.
 */
export function toggleKind(labels, kind) {
  const label = labelForKind(labels, kind);
  if (!label) return labels;
  const checked = (labels || []).filter((l) => l.checked);
  if (label.checked && checked.length === 1) return labels;
  return labels.map((l) => (l.id === label.id ? { ...l, checked: !l.checked } : l));
}

/* ------------------------------------------------------------------ text */

export function plural(n, one, many) {
  return `${n} ${n === 1 ? one : many}`;
}

/** 'Today', 'Tomorrow', 'Yesterday' or the weekday, for the day pane's heading. */
export function relativeDayName(d, today) {
  if (sameDay(d, today)) return 'Today';
  if (sameDay(d, addDays(today, 1))) return 'Tomorrow';
  if (sameDay(d, addDays(today, -1))) return 'Yesterday';
  return d.toLocaleDateString('en-US', { weekday: 'long' });
}

/** The popup summary's orders (one per shipment line), as the card's table rows. */
export function summaryOrders(summary) {
  return (Array.isArray(summary) ? summary : [])
    .map((line) => line?.orders?.[0])
    .filter(Boolean)
    .map((o) => ({ no: o.order_no || '', quantity: o.quantity ?? '', weight: o.weight ?? '' }));
}

export function totalWeight(orders) {
  const total = orders.reduce((n, o) => n + (Number.parseFloat(o.weight) || 0), 0);
  return Math.round(total * 100) / 100;
}
